'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { Share, Plus } from 'lucide-react';

const PWA_PROMPT_KEY = 'winewood_pwa_prompt_dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<'android' | 'ios' | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed or if already running as PWA
    const dismissed = localStorage.getItem(PWA_PROMPT_KEY);
    if (dismissed) return;

    // Check if already installed as PWA
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (isStandalone) return;

    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);

    if (!isIOS && !isAndroid) return;

    if (isIOS) {
      // iOS: only show in Safari (no install prompt API)
      const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);
      if (!isSafari) return;
      setPlatform('ios');
      // Small delay so page loads first
      setTimeout(() => setVisible(true), 1500);
    } else if (isAndroid) {
      setPlatform('android');
      // Listen for beforeinstallprompt
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setTimeout(() => setVisible(true), 1500);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(PWA_PROMPT_KEY, '1');
    setVisible(false);
  };

  const handleInstallAndroid = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setInstalling(false);
    if (choice.outcome === 'accepted') {
      localStorage.setItem(PWA_PROMPT_KEY, '1');
    }
    setVisible(false);
  };

  if (!visible || !platform) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9990]"
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
        onClick={handleDismiss}
      />

      {/* Bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9991] rounded-t-[24px] px-5 pt-5 pb-8"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderBottom: 'none',
          animation: 'pwaSlideUp 0.35s cubic-bezier(0.34,1.2,0.64,1) forwards',
          maxWidth: '430px',
          margin: '0 auto',
        }}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: 'var(--border)' }} />

        {/* Logo + heading */}
        <div className="flex flex-col items-center mb-5">
          <Image
            src="/assets/images/logo_grun_fur_app-1787922846254.png"
            alt="WINEWOOD Adventure"
            width={150}
            height={60}
            className="object-contain mb-3"
          />
          <h2 className="text-lg font-bold text-center" style={{ color: 'var(--card-foreground)' }}>
            Dieses Abenteuer ist so viel besser als Webapp! 🌲
          </h2>
          <p className="text-sm text-center mt-1.5" style={{ color: 'var(--muted-foreground)' }}>
            Schnellerer Zugriff, Offline-Unterstützung und ein echtes App-Erlebnis — direkt auf deinem Homebildschirm.
          </p>
        </div>

        {platform === 'android' && (
          <button
            onClick={handleInstallAndroid}
            disabled={installing}
            className="w-full py-3.5 rounded-pill text-sm font-bold transition-all duration-150 active:scale-[0.98] disabled:opacity-70 mb-3"
            style={{ background: 'linear-gradient(135deg, #7CB342 0%, #9CCC65 100%)', color: '#fff' }}
          >
            {installing ? 'Wird installiert...' : '📲 Zum Homebildschirm hinzufügen'}
          </button>
        )}

        {platform === 'ios' && (
          <div
            className="rounded-app p-4 mb-4 space-y-3"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
              So geht's in 3 Schritten:
            </p>

            {/* Step 1 */}
            <div className="flex items-start gap-3">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                1
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--card-foreground)' }}>
                  Tippe auf <span className="font-bold">Teilen</span>
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                  Das Teilen-Symbol{' '}
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded align-middle"
                    style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}
                  >
                    <Share size={11} color="var(--primary)" />
                  </span>{' '}
                  unten in der Safari-Leiste
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-3">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                2
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--card-foreground)' }}>
                  Nach unten scrollen
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                  Im Menü nach unten wischen bis du die Option siehst
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-3">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                3
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--card-foreground)' }}>
                  Tippe auf <span className="font-bold">„Zum Homebildschirm"</span>
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded align-middle mr-0.5"
                    style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}
                  >
                    <Plus size={11} color="var(--primary)" />
                  </span>{' '}
                  Zum Homebildschirm hinzufügen → Hinzufügen
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dismiss link */}
        <button
          onClick={handleDismiss}
          className="w-full text-center text-xs py-1 transition-colors duration-150"
          style={{ color: 'var(--muted-foreground)' }}
        >
          Im Browser bleiben
        </button>
      </div>

      <style>{`
        @keyframes pwaSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
